const withoutRenderFields = ({ active, icon, ...option }) => option;

export const getLocalizedTitle = (title, language) =>
  title?.[language] || title?.vi || '';

/**
 * Keeps only what should reach the screen: an inactive survey has no questions,
 * and inactive questions or options are dropped rather than greyed out.
 */
export const parseSurveyQuestions = (result) => {
  if (!result?.active) return [];

  return (result.steps || [])
    .filter((step) => step?.active)
    .map((step) => ({
      ...step,
      options: (step.options || []).filter((option) => option?.active),
    }))
    .sort((first, second) => (first.order || 0) - (second.order || 0));
};

/**
 * A dropdown question replaces its answer, a card question toggles one on or
 * off. Toggling the last card off is allowed — the form simply stays
 * incomplete, which the submit button already reflects.
 */
export const selectAnswer = ({ answers, question, option }) => {
  if (!question?.key || !option?.key) return answers;

  const stored = withoutRenderFields(option);
  const current = answers[question.key]?.selectedOptions || [];
  const isSelected = current.some((item) => item.key === stored.key);

  let selectedOptions;
  if (!question.multiSelect) {
    selectedOptions = [stored];
  } else if (isSelected) {
    selectedOptions = current.filter((item) => item.key !== stored.key);
  } else {
    selectedOptions = [...current, stored];
  }

  return {
    ...answers,
    [question.key]: {
      key: question.key,
      order: question.order,
      selectedOptions,
    },
  };
};

export const isFormComplete = (questions, answers, otherTexts) =>
  questions.length > 0 &&
  questions.every((question) => {
    const selected = answers[question.key]?.selectedOptions || [];
    if (!selected.length) return false;

    const hasCustomOption = selected.some((option) => option.isCustom);
    return !hasCustomOption || Boolean(otherTexts[question.key]?.trim());
  });

/**
 * Questions left untouched are omitted so a skipped survey reports only what
 * the user actually answered.
 */
export const buildSurveyPayload = ({ answers, otherTexts, duration }) =>
  Object.entries(answers).reduce((steps, [key, answer]) => {
    const selectedOptions = (answer?.selectedOptions || []).map((option) =>
      option.isCustom
        ? { ...option, customValue: otherTexts[key]?.trim() || '' }
        : { ...option },
    );

    if (!selectedOptions.length) return steps;

    return {
      ...steps,
      [key]: { ...answer, duration, selectedOptions },
    };
  }, {});
